// src/lib/ws.ts
import { MESSAGES_WS as WS_URL } from '../externalUrls'

/**
 * WebSocket Protocol for Django Channels ChatConsumer
 * 
 * Outgoing messages (client -> server):
 * - Send message:  { action: 'send_message', chat_id: string, message: string }
 * - Send audio:    { action: 'send_audio', chat_id: string, audio_data: string (Base64), mime_type: string }
 * - Send image:    { action: 'send_image', chat_id: string, image_data: string (Base64), mime_type: string, caption?: string }
 * - Send document: { action: 'send_document', chat_id: string, document_data: string (Base64), mime_type: string, filename?: string, caption?: string }
 * - Send document batch: { action: 'send_document_batch', chat_id: string, document_ids: number[], caption?: string }
 * - Interrupt:     { action: 'interrupt', chat_id: string }
 * 
 * Incoming messages (server -> client):
 * - Status:   { type: 'status', chat_id: string, message: string, timestamp: string }
 * - Response: { type: 'response', chat_id: string, message: string, sources: [], timestamp: string }
 * - Sources:  { type: 'sources', chat_id: string, sources: [{url,title,domain,cited}], timestamp: string }
 * - Credits:  { type: 'credits', chat_id: string, remaining: number, timestamp: string }
 * - End:      { type: 'end', chat_id: string, timestamp: string }
 * - Error:    { type: 'error', message: string, timestamp: string }
 */

export type Source = {
  url: string;
  title?: string | null;
  domain?: string | null;
  cited?: boolean;
};

type WsIncoming = {
  type: 'status' | 'response' | 'transcription' | 'end' | 'error' | 'sources' | 'credits';
  chat_id?: string;
  message?: string;
  sources?: Source[];
  /** Saldo de créditos restante (solo en frames `credits`). */
  remaining?: number;
  timestamp?: string;
};

// Normalized format for UI components
type WsMessage = {
  role: 'assistant' | 'system' | 'status' | 'transcription';
  text: string;
  timestamp?: string;
  chat_id: string;
  sources?: Source[];
};

type WsOutgoing = {
  action: 'send_message' | 'interrupt' | 'poll_pending';
  chat_id: string;
  message?: string;
};

type WsOutgoingAudio = {
  action: 'send_audio';
  chat_id: string;
  audio_data: string;
  mime_type: string;
};

type WsOutgoingImage = {
  action: 'send_image';
  chat_id: string;
  image_data: string;
  mime_type: string;
  caption?: string;
};

type WsOutgoingDocument = {
  action: 'send_document';
  chat_id: string;
  document_data: string;
  mime_type: string;
  filename?: string;
  caption?: string;
};

type WsOutgoingDocumentBatch = {
  action: 'send_document_batch';
  chat_id: string;
  document_ids: number[];
  caption?: string;
};

type MessageHandler = (msg: WsMessage) => void;
type OpenHandler = () => void;
type CloseHandler = () => void;
type ErrorHandler = (ev: Event) => void;
type SourcesHandler = (chatId: string, sources: Source[]) => void;
type CreditsHandler = (remaining: number) => void;

let ws: WebSocket | null = null;
let isConnecting = false;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
const pendingChatIds = new Set<string>();
const lastPendingPollAt = new Map<string, number>();

let messageHandlers: MessageHandler[] = [];
let openHandlers: OpenHandler[] = [];
let closeHandlers: CloseHandler[] = [];
let errorHandlers: ErrorHandler[] = [];
let sourcesHandlers: SourcesHandler[] = [];
let creditsHandlers: CreditsHandler[] = [];

export function subscribeMessages(handler: MessageHandler) {
  messageHandlers.push(handler);
  return () => {
    messageHandlers = messageHandlers.filter((h) => h !== handler);
  };
}

export function subscribeOpen(handler: OpenHandler) {
  openHandlers.push(handler);
  return () => {
    openHandlers = openHandlers.filter((h) => h !== handler);
  };
}

export function subscribeClose(handler: CloseHandler) {
  closeHandlers.push(handler);
  return () => {
    closeHandlers = closeHandlers.filter((h) => h !== handler);
  };
}

export function subscribeError(handler: ErrorHandler) {
  errorHandlers.push(handler);
  return () => {
    errorHandlers = errorHandlers.filter((h) => h !== handler);
  };
}

/** Suscripción a eventos `sources` (URLs consultadas por el agente, en vivo). */
export function subscribeSources(handler: SourcesHandler) {
  sourcesHandlers.push(handler);
  return () => {
    sourcesHandlers = sourcesHandlers.filter((h) => h !== handler);
  };
}

/** Suscripción al saldo de créditos restante (frames `credits`). */
export function subscribeCredits(handler: CreditsHandler) {
  creditsHandlers.push(handler);
  return () => {
    creditsHandlers = creditsHandlers.filter((h) => h !== handler);
  };
}

export function isWsOpen(): boolean {
  return ws !== null && ws.readyState === WebSocket.OPEN;
}

export function initGlobalSocket() {
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }
  if (isConnecting) return;

  isConnecting = true;
  ws = new WebSocket(WS_URL);

  ws.onopen = () => {
    isConnecting = false;
    reconnectAttempts = 0;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    openHandlers.forEach((h) => h());

    // Recover agent tasks/results that survived a transient disconnect.
    pendingChatIds.forEach((chatId) => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ action: 'poll_pending', chat_id: chatId }));
      }
    });
  };

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data) as WsIncoming;
      
      if (data.type === 'sources') {
        // Live sources: URLs consultadas por el agente durante el turno.
        // Se entregan por un canal propio (no son mensajes de la timeline).
        if (data.chat_id && Array.isArray(data.sources)) {
          sourcesHandlers.forEach((h) => h(data.chat_id!, data.sources!));
        }
        return;
      }

      if (data.type === 'credits') {
        // Saldo de créditos restante: canal propio, sin entrar en la timeline.
        if (typeof data.remaining === 'number') {
          creditsHandlers.forEach((h) => h(data.remaining as number));
        }
        return;
      }

      // Transform Django consumer format to UI format
      let normalized: WsMessage;
      
      if (data.type === 'response') {
        if (data.chat_id) pendingChatIds.delete(data.chat_id);
        // Assistant response message
        normalized = {
          role: 'assistant',
          text: data.message || '',
          timestamp: data.timestamp,
          chat_id: data.chat_id || '',
          sources: data.sources ?? []
        };
      } else if (data.type === 'transcription') {
        normalized = {
          role: 'transcription',
          text: data.message || '',
          timestamp: data.timestamp,
          chat_id: data.chat_id || ''
        };
      } else if (data.type === 'end') {
        if (data.chat_id) pendingChatIds.delete(data.chat_id);
        // End signal - use 'system' role with 'END' text
        normalized = {
          role: 'system',
          text: 'END',
          timestamp: data.timestamp,
          chat_id: data.chat_id || ''
        };
      } else if (data.type === 'status') {
        // Status message (thinking, tool calls, etc.)
        normalized = {
          role: 'status',
          text: data.message || '',
          timestamp: data.timestamp,
          chat_id: data.chat_id || ''
        };
      } else if (data.type === 'error') {
        if (data.chat_id) {
          pendingChatIds.delete(data.chat_id);
        } else {
          pendingChatIds.clear();
        }
        // Error message
        normalized = {
          role: 'system',
          text: `Error: ${data.message || ''}`,
          timestamp: data.timestamp,
          chat_id: data.chat_id || ''
        };
      } else {
        console.warn('Unknown message type from WebSocket:', data);
        return;
      }
      
      messageHandlers.forEach((h) => h(normalized));
    } catch (err) {
      console.error('Error parseando mensaje de WS:', err);
    }
  };

  ws.onclose = () => {
    isConnecting = false;
    closeHandlers.forEach((h) => h());
    ws = null;
    scheduleReconnect();
  };

  ws.onerror = (ev) => {
    errorHandlers.forEach((h) => h(ev));
  };
}

function scheduleReconnect() {
  if (reconnectTimer || isConnecting) return;
  if (pendingChatIds.size === 0 && messageHandlers.length === 0) return;

  const delay = Math.min(1000 * 2 ** reconnectAttempts, 10000);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    initGlobalSocket();
  }, delay);
}

export async function sendWsMessage(out: WsOutgoing | WsOutgoingAudio | WsOutgoingImage | WsOutgoingDocument | WsOutgoingDocumentBatch, timeout = 5000): Promise<void> {
  if (out.action === 'interrupt') {
    pendingChatIds.delete(out.chat_id);
  } else if (out.action !== 'poll_pending') {
    pendingChatIds.add(out.chat_id);
  }

  // Si ya hay ws abierto, enviamos de inmediato
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(out));
    return;
  }

  // Aseguramos que exista el socket (inicia si es necesario)
  initGlobalSocket();

  // Si mientras iniciamos el socket ya quedó abierto, enviamos y retornamos
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(out));
    return;
  }

  // Esperamos a que abra (suscribiéndonos temporalmente a openHandlers)
  return new Promise<void>((resolve, reject) => {
    let settled = false;

    const onOpen = () => {
      if (settled) return;
      settled = true;
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(out));
          resolve();
        } else {
          reject(new Error('WebSocket no está abierto tras onOpen'));
        }
      } catch (err) {
        reject(err);
      } finally {
        // limpiar subs
        unsubscribeOpen();
        unsubscribeErr();
        clearTimer();
      }
    };

    const onError = (ev: Event) => {
      if (settled) return;
      settled = true;
      reject(new Error('Error en WebSocket al abrir'));
      unsubscribeOpen();
      unsubscribeErr();
      clearTimer();
    };

    // Subs temporales
    const unsubscribeOpen = subscribeOpen(onOpen);
    const unsubscribeErr = subscribeError(onError);

    // Timeout para no quedarse esperando indefinidamente
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsubscribeOpen();
      unsubscribeErr();
      reject(new Error('Timeout esperando apertura de WebSocket'));
    }, timeout);

    function clearTimer() {
      clearTimeout(timer);
    }

    // Nota: si el socket ya se abrió entre el initGlobalSocket() y aquí,
    // el onOpen no será llamado; por eso revisamos nuevamente:
    if (ws && ws.readyState === WebSocket.OPEN && !settled) {
      settled = true;
      try {
        ws.send(JSON.stringify(out));
        unsubscribeOpen();
        unsubscribeErr();
        clearTimer();
        resolve();
      } catch (err) {
        unsubscribeOpen();
        unsubscribeErr();
        clearTimer();
        reject(err);
      }
    }
  });
}

/**
 * Helper function to send a chat message
 * Converts simple parameters to the protocol format
 */
export function sendChatMessage(chatId: string, text: string): Promise<void> {
  return sendWsMessage({
    action: 'send_message',
    chat_id: chatId,
    message: text
  });
}

/**
 * Helper function to interrupt a chat response
 */
export function sendInterrupt(chatId: string): Promise<void> {
  return sendWsMessage({
    action: 'interrupt',
    chat_id: chatId
  });
}

/** Ask the backend to reattach/deliver an in-flight or stored result. */
export function pollPending(chatId: string): Promise<void> {
  const now = Date.now();
  const lastPoll = lastPendingPollAt.get(chatId) ?? 0;
  if (now - lastPoll < 1000) return Promise.resolve();
  lastPendingPollAt.set(chatId, now);

  pendingChatIds.add(chatId);
  // When the socket is still opening, ws.onopen already polls every pending
  // chat. Calling sendWsMessage here as well would send the same poll twice.
  if (!isWsOpen()) {
    initGlobalSocket();
    return Promise.resolve();
  }
  return sendWsMessage({
    action: 'poll_pending',
    chat_id: chatId,
  });
}

/**
 * Helper function to send an audio (voice note) message
 * @param chatId - Chat UUID
 * @param audioData - Base64-encoded audio data (without the data: URL prefix)
 * @param mimeType - MIME type of the audio (e.g. 'audio/webm')
 */
export function sendAudioMessage(
  chatId: string,
  audioData: string,
  mimeType: string
): Promise<void> {
  return sendWsMessage({
    action: 'send_audio',
    chat_id: chatId,
    audio_data: audioData,
    mime_type: mimeType,
  } as WsOutgoingAudio);
}

/**
 * Helper function to send an image message
 * @param chatId - Chat UUID
 * @param imageData - Base64-encoded image data (without the data: URL prefix)
 * @param mimeType - MIME type of the image (e.g. 'image/jpeg', 'image/png')
 * @param caption - Optional text caption to send with the image
 */
export function sendImageMessage(
  chatId: string,
  imageData: string,
  mimeType: string,
  caption?: string
): Promise<void> {
  return sendWsMessage({
    action: 'send_image',
    chat_id: chatId,
    image_data: imageData,
    mime_type: mimeType,
    caption: caption || '',
  } as WsOutgoingImage);
}

/**
 * Helper function to send a document/PDF message
 * @param chatId - Chat UUID
 * @param documentData - Base64-encoded document data (without the data: URL prefix)
 * @param mimeType - MIME type (e.g. 'application/pdf')
 * @param filename - Optional filename (e.g. 'contrato.pdf')
 * @param caption - Optional text caption to send with the document
 */
export function sendDocumentMessage(
  chatId: string,
  documentData: string,
  mimeType: string,
  filename?: string,
  caption?: string
): Promise<void> {
  return sendWsMessage({
    action: 'send_document',
    chat_id: chatId,
    document_data: documentData,
    mime_type: mimeType,
    filename: filename || 'document.pdf',
    caption: caption || '',
  } as WsOutgoingDocument);
}

export function sendDocumentBatch(
  chatId: string,
  documentIds: number[],
  caption?: string,
): Promise<void> {
  return sendWsMessage({
    action: 'send_document_batch',
    chat_id: chatId,
    document_ids: documentIds,
    caption: caption || '',
  } as WsOutgoingDocumentBatch);
}
